import type React from 'react';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	usePageScrollPosition,
	usePromoteCtaDisplayed,
	useUnauthenticatedSignForm,
} from '#src/app/pages/petitionDetails/shared/hooks';
import type { PageScrollPosition } from '#src/app/pages/petitionDetails/shared/hooks';

import { PetitionStatusHeaderContainer } from '../PetitionStatusHeader';
import { SignatureStatsClosedContainer, SignatureStatsVictoryContainer } from '../stats/signatureStats';

import { MobileComboButtonContainer } from './MobileComboButton';
import { MobileRespondButtonContainer } from './MobileRespondButton';
import { MobileShareButtonContainer } from './MobileShareButton';
import { MobileSignButtonContainer } from './MobileSignButton';

export type ActionPanelVariant = 'v1' | 'v2';
export type ActionPanelLayout = 'inline-then-sticky' | 'sticky-sign';

type Props = {
	layout?: ActionPanelLayout;
	sticky: boolean;
	variant: ActionPanelVariant;
};

const calculateVisibility = (
	layout: ActionPanelLayout,
	sticky: boolean,
	{ beforeSuggestedPetitions, afterDescription, afterMobileActionButton }: PageScrollPosition,
): boolean => {
	if (!sticky) {
		return layout === 'inline-then-sticky';
	}

	/**
	 * The standard sticky button appears after the non-sticky button. A sticky-only layout starts visible instead.
	 * !afterDescription condition allows sticky button to show in petitions with short descriptions
	 */
	const hasReachedStickyStart = layout === 'sticky-sign' || !!afterMobileActionButton;

	return hasReachedStickyStart && (beforeSuggestedPetitions || !afterDescription);
};

export const MobileActionButtonContainer: React.FC<Props> = ({ layout = 'inline-then-sticky', sticky, variant }) => {
	const usePageScrollPositionContext = usePageScrollPosition();
	const {
		data: {
			petition: { status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { decisionMaker, signed },
	} = usePetitionDetailsPageUserData();
	const promoteCtaDisplayed = usePromoteCtaDisplayed();
	const {
		data: { enabled: unauthenticatedSignFormEnabled },
	} = useUnauthenticatedSignForm();

	const visible = calculateVisibility(layout, sticky, usePageScrollPositionContext);

	if (decisionMaker) {
		return <MobileRespondButtonContainer visible={visible} sticky={sticky} />;
	}

	if (status === 'CLOSED') {
		return (
			<div className="mb-6 flex flex-col gap-5">
				{!sticky && (
					<>
						<PetitionStatusHeaderContainer />
						<div className="text-center">
							<SignatureStatsClosedContainer />
						</div>
					</>
				)}
				<MobileShareButtonContainer visible={visible} sticky={sticky} hideSocialProof />
			</div>
		);
	}

	if (status === 'VICTORY') {
		return (
			<div className="mb-6 flex flex-col gap-5">
				{!sticky && (
					<>
						<PetitionStatusHeaderContainer />
						<SignatureStatsVictoryContainer textAlign="center" />
					</>
				)}
				<MobileShareButtonContainer visible={visible} sticky={sticky} hideSocialProof />
			</div>
		);
	}

	if (signed && !unauthenticatedSignFormEnabled) {
		return promoteCtaDisplayed ? (
			<MobileComboButtonContainer visible={visible} sticky={sticky} />
		) : (
			<MobileShareButtonContainer visible={visible} sticky={sticky} />
		);
	}

	return (
		<MobileSignButtonContainer
			hideShareAffordance={layout === 'sticky-sign'}
			visible={visible}
			sticky={sticky}
			variant={variant}
		/>
	);
};
